import { createBrowserClient } from "@supabase/ssr";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { syncLogtailUser } from "../logtail";
import { syncBetterStackUser } from "../betterstack-tag";
import { Logger } from "../logger";
import { identifyPostHogUser, resetPostHogUser } from "../posthog";
import { getRequiredSupabaseClientConfig } from "./config";

let supabaseClient: SupabaseClient | null = null;
let supabaseConfigLogged = false;

function syncAnalyticsState(session: Session | null, resetPostHog = false) {
  const user_id = session?.user?.id ?? null;
  Logger.setuser_id(user_id);
  syncLogtailUser(session?.user ?? null);
  syncBetterStackUser(session?.user ?? null);
  if (user_id) {
    // `email` is set as a person property so PostHog renders the user's email
    // in the Person/User column instead of the Supabase user id.
    identifyPostHogUser(user_id, { email: session?.user?.email });
  } else if (resetPostHog) {
    resetPostHogUser();
  }
}

export function createClient(): SupabaseClient {
  if (!supabaseClient) {
    const { url, key } = getRequiredSupabaseClientConfig();
    if (!supabaseConfigLogged) {
      supabaseConfigLogged = true;
      const keyPreview = key.length <= 8
        ? `${key[0] ?? ''}***(len=${key.length})`
        : `${key.slice(0, 6)}…${key.slice(-4)} (len=${key.length})`;
      Logger.info('Supabase browser config resolved', {
        supabaseUrl: url,
        supabasePublishableKey: keyPreview,
      });
    }
    supabaseClient = createBrowserClient(url, key);

    supabaseClient.auth.getSession().then(({ data }) => {
      syncAnalyticsState(data.session ?? null);
    });

    supabaseClient.auth.onAuthStateChange((event, session) => {
      Logger.debug("Auth state changed", { event, hasSession: !!session });
      syncAnalyticsState(session, event === 'SIGNED_OUT');
    });
  }
  return supabaseClient;
}
